// utils/realtimeManager/subscription/retry.ts — retry scheduling with visibility-change recovery
import { BASE_DELAY } from '../types.js';
import { MAX_DELAY } from '../types.js';
import { MAX_RETRIES } from '../types.js';
import { VISIBILITY_COOLDOWN } from '../types.js';

interface RetrySchedulerOpts {
  isUnsubscribed: () => boolean;
  subscribe: () => void;
  channelName: string;
}

export interface RetryScheduler {
  scheduleRetry: () => void;
  reset: () => void;
  dispose: () => void;
  removeVisibilityHandler: () => void;
}

export function createRetryScheduler(opts: RetrySchedulerOpts): RetryScheduler {
  let retryCount = 0;
  let retryTimeout: ReturnType<typeof setTimeout> | null = null;
  let visibilityHandler: (() => void) | null = null;
  let lastMaxRetriesTime = 0;

  const removeVisibilityHandler = () => {
    if (visibilityHandler) {
      document.removeEventListener('visibilitychange', visibilityHandler);
      visibilityHandler = null;
    }
  };

  const scheduleRetry = () => {
    if (opts.isUnsubscribed()) return;

    if (retryCount >= MAX_RETRIES) {
      const now = Date.now();
      if (!visibilityHandler && (now - lastMaxRetriesTime > VISIBILITY_COOLDOWN || lastMaxRetriesTime === 0)) {
        lastMaxRetriesTime = now;
        console.error(`❌ Realtime max retries reached: ${opts.channelName}. Will retry on next visibility change.`);
        visibilityHandler = () => {
          if (document.visibilityState === 'visible' && !opts.isUnsubscribed()) {
            removeVisibilityHandler();
            retryCount = 0;
            opts.subscribe();
          }
        };
        document.addEventListener('visibilitychange', visibilityHandler);
      }
      return;
    }

    // Clear any pending retry before scheduling a new one
    if (retryTimeout) {
      clearTimeout(retryTimeout);
      retryTimeout = null;
    }

    retryCount++;
    const delay = Math.min(BASE_DELAY * Math.pow(2, retryCount - 1), MAX_DELAY);
    const jitter = Math.random() * 1000;

    if (import.meta.env.DEV) {
      console.log(`🔄 Realtime retry ${retryCount}/${MAX_RETRIES} for ${opts.channelName} in ${Math.round(delay + jitter)}ms`);
    }

    retryTimeout = setTimeout(() => {
      retryTimeout = null;
      if (!opts.isUnsubscribed()) {
        opts.subscribe();
      }
    }, delay + jitter);
  };

  const reset = () => {
    retryCount = 0;
  };

  const dispose = () => {
    removeVisibilityHandler();
    if (retryTimeout) {
      clearTimeout(retryTimeout);
      retryTimeout = null;
    }
  };

  return { scheduleRetry, reset, dispose, removeVisibilityHandler };
}
